'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  fulfillmentRequestDeclineMutation,
  parseFulfillmentRequestFailureKind,
  type FulfillmentRequestFailureKind,
} from '@entities/fulfillment-request'
import { faRotateLeft, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { Alert, AlertDescription, AlertTitle } from '@ui/base/alert'
import { Button } from '@ui/base/button'
import { FieldError } from '@ui/base/field'

type NonConflictFailureKind = Exclude<FulfillmentRequestFailureKind, 'conflict'>

const failureMessageByKind: Record<NonConflictFailureKind, string> = {
  validation: 'Сервер не принял действие. Обновите входящие, проверьте состояние заявки и повторите.',
  unauthorized: 'Сессия истекла. Войдите заново, откройте входящие и повторите действие.',
  forbidden: 'Изменить эту заявку нельзя. Вернитесь во входящие и выберите доступную заявку.',
  'not-found': 'Заявка не найдена. Обновите входящие и проверьте, осталась ли она в списке.',
  'too-many-requests': 'Слишком много попыток. Подождите несколько минут и повторите действие.',
  unknown: 'Сервис временно недоступен. Проверьте соединение и повторите действие позже.',
}

/**
 * Отказ убирает заявку из новых в раздел отклонённых: она не мешает работать, но остаётся
 * доступной — по кнопке «Вернуть в новые» заявка возвращается обратно.
 */
export const DeclineRequestButton = ({
  requestId,
  expectedRevision,
  isDeclined,
  onDone,
}: {
  requestId: string
  expectedRevision: number
  isDeclined: boolean
  onDone?: (isDeclined: boolean) => void
}) => {
  const router = useRouter()
  const declineRequest = useMutation(fulfillmentRequestDeclineMutation())
  const [failureKind, setFailureKind] = useState<FulfillmentRequestFailureKind | null>(null)

  const send = async () => {
    setFailureKind(null)
    try {
      const nextIsDeclined = await declineRequest.mutateAsync(
        isDeclined
          ? { id: requestId, isDeclined: true }
          : { id: requestId, isDeclined: false, request: { expected_revision: expectedRevision } }
      )
      onDone?.(nextIsDeclined)
      router.refresh()
    } catch (error) {
      setFailureKind(parseFulfillmentRequestFailureKind(error))
    }
  }

  const refreshRequests = () => {
    setFailureKind(null)
    router.refresh()
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant={isDeclined ? 'outline' : 'destructive-outline'}
          size="lg"
          disabled={declineRequest.isPending}
          onClick={() => void send()}
        >
          <FontAwesomeIcon icon={isDeclined ? faRotateLeft : faXmark} />
          {isDeclined ? 'Вернуть в новые' : 'Отклонить заявку'}
        </Button>
        {failureKind !== null && failureKind !== 'conflict' && (
          <FieldError>{failureMessageByKind[failureKind]}</FieldError>
        )}
      </div>
      {failureKind === 'conflict' && (
        <Alert variant="warning">
          <AlertTitle>Заявка уже разобрана или изменилась</AlertTitle>
          <AlertDescription>
            <p>Обновите заявки и проверьте её состояние перед повторным действием.</p>
            <Button type="button" variant="outline" onClick={refreshRequests}>
              Обновить заявки
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
